import React, { useState, useEffect, useCallback } from 'react';
import { DmsTextField, DmsButton, fetchData, HTTP_STATUS } from 'dmsbasereact';
import { DmsAutocomplete } from '../../DmsAutoComplete/DmsAutoComplete';
import { Grid, Typography, Box, IconButton, Tooltip, Divider, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const ProductRow = ({ product, index, batchesWithPart, updateProduct, removeProduct, canRemove, intl, ownerEntityPk, isCheckout, stockRefreshKey, fieldHasError, helperText, selectedBatchPks }) => {
    const [availableStock, setAvailableStock] = useState(null);
    const [isLoadingStock, setIsLoadingStock] = useState(false);

    // Hide expired batches (past today), warn batches expiring within 6 months
    const now = new Date();
    const sixMonthsFromNow = new Date(now);
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

    // If the product has a pre-filled batchPk (e.g. from XML import) that isn't in the
    // options list yet (stock not loaded), add it as a synthetic option so the select renders correctly
    const syntheticBatch = (product.batchPk && !batchesWithPart.find(b => b.pk === product.batchPk))
        ? [{ pk: product.batchPk, number: product.batchNumber || String(product.batchPk), part_name: product.partName || '', expiration: null }]
        : [];

    const batchOptions = [...batchesWithPart, ...syntheticBatch]
        .filter(b => {
            if (!b.expiration) return true;
            return new Date(b.expiration) > now;
        })
        .map(b => {
            const expirationDate = b.expiration ? new Date(b.expiration) : null;
            const expirationFormatted = expirationDate ? expirationDate.toLocaleDateString('pt-BR') : '';
            const isExpiringSoon = expirationDate && expirationDate <= sixMonthsFromNow;

            return {
                value: b.pk,
                label: `${b.number} - ${b.part_name}${b.part_description ? ` (${b.part_description})` : ''}`,
                expiration: expirationFormatted,
                isExpiringSoon,
            };
        });

    // Fetch available stock when batch, owner entity, or refresh key changes (only for checkout)
    const fetchStock = useCallback(async () => {
        if (!isCheckout || !product.batchPk || !ownerEntityPk) {
            setAvailableStock(null);
            return;
        }

        setIsLoadingStock(true);
        try {
            const response = await fetchData(
                `/batch_location/quantity/by-location?batch_pk=${product.batchPk}&organization_branch_pk=${ownerEntityPk}`,
                'GET',
                true
            );
            if (response.status === HTTP_STATUS.OK_200) {
                setAvailableStock(response.data.quantity);
            } else {
                setAvailableStock(0);
            }
        } catch {
            setAvailableStock(0);
        } finally {
            setIsLoadingStock(false);
        }
    }, [product.batchPk, ownerEntityPk, isCheckout, stockRefreshKey]);

    useEffect(() => {
        fetchStock();
    }, [fetchStock]);

    useEffect(() => {
        if (isCheckout && availableStock !== null) {
            updateProduct(product.id, 'availableStock', availableStock);
        }
    }, [availableStock, isCheckout, product.id, updateProduct]);

    const handleQuantityChange = (e) => {
        updateProduct(product.id, 'quantity', e.target.value);
    };

    const handleBatchChange = (val) => {
        updateProduct(product.id, 'batchPk', val);
        updateProduct(product.id, 'quantity', '');
    };

    // Get selected batch info for display
    const selectedBatch = batchesWithPart.find(b => b.pk === product.batchPk);

    return (
        <Box sx={{ mb: 2 }}>
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <DmsAutocomplete
                                key={`batch-${product.id}-${batchOptions.length}`}
                                label={intl.get('Batch').d('Batch') + (fieldHasError ? " *" : "")}
                                value={product.batchPk}
                                onChange={handleBatchChange}
                                options={batchOptions.length > 0
                                    ? [{ value: '', label: intl.get('SelectBatch').d('Select batch') }, ...batchOptions]
                                    : [{ value: '', label: intl.get('NoBatchesRegistered').d('No batches registered.'), disabled: true }]
                                }
                                getOptionDisabled={(option) =>
                                    option.value !== '' && selectedBatchPks.includes(option.value)
                                }
                                slotProps={{
                                    popper: { sx: { minWidth: 500 } },
                                    paper: { sx: { minWidth: 500 } },
                                }}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                            <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem' }}>
                                                {option.label}
                                            </Typography>
                                            {option.expiration && (
                                                <Chip
                                                    label={option.isExpiringSoon
                                                        ? `${intl.get('Batch.ExpiringSoon').d('Expires soon')}: ${option.expiration}`
                                                        : `${intl.get('Batch.Expiration').d('Exp')}: ${option.expiration}`
                                                    }
                                                    size="small"
                                                    color={option.isExpiringSoon ? "warning" : "default"}
                                                    variant={option.isExpiringSoon ? "filled" : "outlined"}
                                                    sx={{ fontSize: '0.7rem', height: 22, flexShrink: 0 }}
                                                />
                                            )}
                                        </Box>
                                    </li>
                                )}
                                fullWidth
                                error={fieldHasError?.('checkout_batchPk')}
                                helperText={helperText?.('checkout_batchPk')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <DmsTextField
                                name={`partName-${product.id}`}
                                label={intl.get('Product').d('Product')}
                                value={product.partName || selectedBatch?.part_name || ''}
                                disabled
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <DmsTextField
                                name={`quantity-${product.id}`}
                                label={intl.get('Quantity').d('Quantity') + (fieldHasError ? " *" : "")}
                                value={product.quantity}
                                onChange={handleQuantityChange}
                                type="number"
                                fullWidth
                                inputProps={{
                                    min: 1,
                                }}
                                error={fieldHasError?.('checkout_quantity')}
                                helperText={helperText?.('checkout_quantity')}
                            />
                            {isCheckout && product.batchPk && ownerEntityPk && (
                                <Box sx={{ mt: 0.5 }}>
                                    {isLoadingStock ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {intl.get('LoadingStock').d('Loading stock...')}
                                        </Typography>
                                    ) : availableStock !== null ? (
                                        <>
                                            <Chip
                                                icon={<InventoryIcon />}
                                                label={`${intl.get('AvailableStock').d('Available')}: ${availableStock}`}
                                                size="small"
                                                color={parseInt(product.quantity, 10) > availableStock ? "warning" : availableStock > 0 ? "success" : "error"}
                                                variant="outlined"
                                            />
                                            {parseInt(product.quantity, 10) > availableStock && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                    <WarningAmberIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                                    <Typography variant="caption" color="error">
                                                        {intl.get('Checkout.InsufficientStockWarning').d('Quantity exceeds available stock. This will generate a notice.')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </>
                                    ) : null}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    {selectedBatch?.part_description && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {selectedBatch.part_description}
                        </Typography>
                    )}
                </Box>
                {canRemove && (
                    <Tooltip title={intl.get('RemoveProduct').d('Remove Product')}>
                        <IconButton
                            onClick={() => removeProduct(product.id)}
                            color="error"
                            size="small"
                            sx={{ mt: 1 }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

export const ProductDetailsSection = ({
    products,
    addProduct,
    removeProduct,
    updateProduct,
    batchesWithPart = [],
    intl,
    ownerEntityPk,
    isCheckin,
    stockRefreshKey,
    fieldHasError,
    helperText,
}) => {
    const isCheckout = !isCheckin;

    return (
        <Box sx={{ mt: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {intl.get('ProductDetails').d('Product Details')}
                </Typography>
                <DmsButton
                    onClick={addProduct}
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ fontSize: '0.1vw', padding: '4px 12px' }}
                >
                    {intl.get('AddProduct').d('Add Product')}
                </DmsButton>
            </Box>
            {products.map((product, index) => {
                const selectedBatchPks = products
                    .filter((p, i) => i !== index && p.batchPk)
                    .map(p => p.batchPk);
                return (
                    <ProductRow
                        key={product.id}
                        product={product}
                        index={index}
                        batchesWithPart={batchesWithPart}
                        updateProduct={updateProduct}
                        removeProduct={removeProduct}
                        canRemove={products.length > 1}
                        intl={intl}
                        ownerEntityPk={ownerEntityPk}
                        isCheckout={isCheckout}
                        stockRefreshKey={stockRefreshKey}
                        fieldHasError={fieldHasError}
                        helperText={helperText}
                        selectedBatchPks={selectedBatchPks}
                    />
                );
            })}
            <Divider sx={{ mt: 2 }} />
        </Box>
    );
};
